import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, UserPlus } from "lucide-react";
import { PHYSREF } from "@/constants/testIds";
import { NUCC_SYSTEM, listPhysicianReferrals } from "@/lib/physicianReferralApi";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to load physician referral catalog.";
}

export default function PhysicianReferralsPage() {
  const [items, setItems] = useState([]);
  const [system, setSystem] = useState(NUCC_SYSTEM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listPhysicianReferrals({ active: true });
        if (cancelled) return;
        setItems(data.items || []);
        setSystem(data.system || NUCC_SYSTEM);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="emr-catalog" data-testid={PHYSREF.page}>
      <div className="emr-catalog__pagehead">
        <h1 className="emr-catalog__title" data-testid={PHYSREF.title}>
          Physician Referral Catalog
        </h1>
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section emr-catalog__purpose">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Development/demo physician-specialty master for future Encounter referrals.
              Not a patient referral list and not an ordering screen. PT/OT belong in a
              separate therapy catalog.
            </p>
          </section>

          <section
            data-testid={PHYSREF.banner}
            className="card emr-catalog__section emr-catalog__notice emr-catalog__section--notice"
          >
            <h2 className="emr-catalog__section-title">Reference data</h2>
            <p className="emr-catalog__copy">
              Small demo subset only. Each row is catalog/reference data ({system}). Not a
              FHIR ServiceRequest.
            </p>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Physician specialties</h2>
          {error ? (
            <div data-testid={PHYSREF.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button
                variant="outline"
                className="btn emr-catalog__retry"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={PHYSREF.loading} className="emr-catalog__status-msg">
              Loading physician referral catalog…
            </div>
          ) : !items.length ? (
            <div data-testid={PHYSREF.emptyState} className="emr-catalog__empty">
              <UserPlus className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No demo physician specialties are seeded yet.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={PHYSREF.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Specialty</th>
                    <th>Short Name</th>
                    <th>Category</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.id || row.catalogKey}
                      data-testid={`${PHYSREF.row}-${row.id}`}
                    >
                      <td className="emr-catalog__name">{row.specialtyName}</td>
                      <td>{row.shortName}</td>
                      <td className="emr-catalog__muted">{row.category || "—"}</td>
                      <td>
                        {row.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Active</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
